<script lang="ts" context="module">
  export const regionNudgeInfo = writable<{
    shouldDisplay: boolean;
    countryName: string | undefined;
    dismissed: boolean;
  }>({ shouldDisplay: false, countryName: undefined, dismissed: false });
</script>

<script lang="ts">
  import { getOptimalLocation, region } from '$lib/region';
  import Button from './ui/button.svelte';
  import Icon from './ui/icon/icon.svelte';
  import { t } from '$lib/i18n';
  import { defaultLang, lang } from '$lib/lang';
  import { browser } from '$app/environment';
  import { geolocation } from '$lib/stores/geolocation';
  import { buildPath } from '$lib/utils/paths';
  import { slide } from 'svelte/transition';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { writable } from 'svelte/store';
  import { isSupportedUSCountry, optimalUSCountry } from '$lib/country';

  /**
    REGION NUDGE
    We will suggest a change of store if the user is not in its optimal location.

    - If the user is in the UK, Jersey and Guernsey
    -> suggest UK if not already there

    - all EU countries, Turkey and Russia
    -> suggest EU if not already there

    - else
    -> suggest US if not already there
  */

  $: currentRegion = $region;
  $: currentCountry = $geolocation?.country;
  $: optimalLocation = getOptimalLocation(currentCountry);

  $: regionNames = new Intl.DisplayNames([$lang], { type: 'region' });
  $: countryName = currentCountry ? regionNames.of(currentCountry) : undefined;

  $: dismissed = browser && sessionStorage.getItem('region-nudge-dismissed') === optimalLocation;

  $: shouldDisplay =
    browser && !dismissed && optimalLocation && currentRegion !== optimalLocation && countryName;

  $: if (browser && typeof shouldDisplay === 'boolean') {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
  }

  $: if (shouldDisplay || countryName) {
    regionNudgeInfo.update((info) => ({
      ...info,
      shouldDisplay: !!(shouldDisplay && countryName) || false,
      countryName,
      dismissed
    }));
  }
</script>

{#if shouldDisplay && countryName}
  <div transition:slide={{ duration: $reducedMotion ? 0 : 250, axis: 'y' }} class="bg-background">
    <div class="bg-primary/4 border-b px-2 lg:px-8 py-2 lg:py-4">
      <div
        class="container mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      >
        <div class="flex flex-row items-center gap-1 sm:gap-3">
          <Icon icon="language" size="md" />
          <div>
            <p class="text-md">
              {$t('region-nudge.title', { country: countryName })}
            </p>
            <p class="text-primary/40 mt-0.5 hidden sm:block">
              {$t('region-nudge.description', { region: optimalLocation.toUpperCase() })}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            name="change-to-optimal-region"
            data-sveltekit-reload
            as="a"
            class="flex-1 sm:flex-auto"
            size="sm"
            href={$buildPath({
              region: optimalLocation,
              lang: defaultLang
            })}
            on:click={() => {
              if (currentCountry && isSupportedUSCountry(currentCountry)) {
                optimalUSCountry.set(currentCountry);
              } else {
                optimalUSCountry.set(null);
              }
            }}
          >
            {$t('region-nudge.button', { region: optimalLocation.toUpperCase() })}
          </Button>
          <button
            aria-label="dismiss"
            class="w-10 h-10 inline-flex items-center justify-center hover:bg-primary/4 rounded-sm transition-all border"
            on:click={() => {
              if (optimalLocation) {
                sessionStorage.setItem('region-nudge-dismissed', optimalLocation);
              }

              dismissed = true;
            }}
          >
            <Icon size="md" icon="cross" />
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}
